import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { BookOpen, FileUp, Pencil, Plus, RefreshCw, Search, Trash2 } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_CATEGORIES = [
  "Hours",
  "Parking",
  "Wi-Fi",
  "Amenities",
  "Policies",
  "Accessibility",
  "Events",
  "Movies",
  "Dining",
  "Shopping",
  "Safety",
  "Other",
];

const emptyFaq = {
  question: "",
  answer: "",
  category: "Other",
  tags: "",
  sort_order: 0,
  is_active: true,
};

const emptyArticle = {
  title: "",
  summary: "",
  body_markdown: "",
  category: "Other",
  tags: "",
  sort_order: 0,
  is_active: true,
};

const parseTags = (value) =>
  String(value || "")
    .split(",")
    .map((tag) => tag.trim())
    .filter(Boolean);

const formatTags = (tags) => (Array.isArray(tags) ? tags.join(", ") : "");

export default function KnowledgeBase() {
  const { user, getAuthHeader } = useAuth();
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [faqs, setFaqs] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [faqSearch, setFaqSearch] = useState("");
  const [articleSearch, setArticleSearch] = useState("");
  const [faqDialogOpen, setFaqDialogOpen] = useState(false);
  const [articleDialogOpen, setArticleDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [editingArticle, setEditingArticle] = useState(null);
  const [faqForm, setFaqForm] = useState(emptyFaq);
  const [articleForm, setArticleForm] = useState(emptyArticle);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadMeta, setUploadMeta] = useState({
    title: "",
    summary: "",
    category: "Other",
    tags: "",
    sort_order: 0,
    is_active: true,
  });
  const [uploading, setUploading] = useState(false);
  const [previewMarkdown, setPreviewMarkdown] = useState("");

  const canEdit = ["admin", "manager"].includes(user?.role);
  const canDelete = user?.role === "admin";

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [faqRes, articleRes, categoryRes] = await Promise.all([
        axios.get(`${API_URL}/api/faqs`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/knowledge/articles`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/knowledge/categories`, { headers: getAuthHeader() }),
      ]);
      setFaqs(faqRes.data || []);
      setArticles(articleRes.data || []);
      if (categoryRes.data?.categories?.length) {
        setCategories(categoryRes.data.categories);
      }
    } catch (error) {
      toast.error("Failed to load knowledge base");
    } finally {
      setLoading(false);
    }
  };

  const resetFaqForm = () => {
    setFaqForm(emptyFaq);
    setEditingFaq(null);
  };

  const resetArticleForm = () => {
    setArticleForm(emptyArticle);
    setEditingArticle(null);
  };

  const openEditFaq = (faq) => {
    setEditingFaq(faq);
    setFaqForm({
      question: faq.question || "",
      answer: faq.answer || "",
      category: faq.category || "Other",
      tags: formatTags(faq.tags),
      sort_order: faq.sort_order || 0,
      is_active: faq.is_active !== false,
    });
    setFaqDialogOpen(true);
  };

  const openEditArticle = (article) => {
    setEditingArticle(article);
    setArticleForm({
      title: article.title || "",
      summary: article.summary || "",
      body_markdown: article.body_markdown || "",
      category: article.category || "Other",
      tags: formatTags(article.tags),
      sort_order: article.sort_order || 0,
      is_active: article.is_active !== false,
    });
    setArticleDialogOpen(true);
  };

  const submitFaq = async (event) => {
    event.preventDefault();
    const payload = {
      question: faqForm.question.trim(),
      answer: faqForm.answer.trim(),
      category: faqForm.category,
      tags: parseTags(faqForm.tags),
      sort_order: Number(faqForm.sort_order) || 0,
      is_active: Boolean(faqForm.is_active),
    };
    try {
      if (editingFaq) {
        await axios.put(`${API_URL}/api/faqs/${editingFaq.id}`, payload, {
          headers: getAuthHeader(),
        });
        toast.success("FAQ updated");
      } else {
        await axios.post(`${API_URL}/api/faqs`, payload, {
          headers: getAuthHeader(),
        });
        toast.success("FAQ created");
      }
      setFaqDialogOpen(false);
      resetFaqForm();
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "FAQ save failed");
    }
  };

  const submitArticle = async (event) => {
    event.preventDefault();
    const payload = {
      title: articleForm.title.trim(),
      summary: articleForm.summary.trim(),
      body_markdown: articleForm.body_markdown.trim(),
      category: articleForm.category,
      tags: parseTags(articleForm.tags),
      sort_order: Number(articleForm.sort_order) || 0,
      is_active: Boolean(articleForm.is_active),
    };
    try {
      if (editingArticle) {
        await axios.put(`${API_URL}/api/knowledge/articles/${editingArticle.id}`, payload, {
          headers: getAuthHeader(),
        });
        toast.success("Article updated");
      } else {
        await axios.post(`${API_URL}/api/knowledge/articles`, payload, {
          headers: getAuthHeader(),
        });
        toast.success("Article created");
      }
      setArticleDialogOpen(false);
      resetArticleForm();
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Article save failed");
    }
  };

  const deleteFaq = async (faqId) => {
    if (!window.confirm("Delete this FAQ?")) return;
    try {
      await axios.delete(`${API_URL}/api/faqs/${faqId}`, { headers: getAuthHeader() });
      toast.success("FAQ deleted");
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Delete failed");
    }
  };

  const deleteArticle = async (articleId) => {
    if (!window.confirm("Delete this article?")) return;
    try {
      await axios.delete(`${API_URL}/api/knowledge/articles/${articleId}`, {
        headers: getAuthHeader(),
      });
      toast.success("Article deleted");
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Delete failed");
    }
  };

  const previewConvert = async () => {
    if (!uploadFile) {
      toast.error("Choose a document first");
      return;
    }
    const formData = new FormData();
    formData.append("file", uploadFile);
    try {
      const response = await axios.post(`${API_URL}/api/knowledge/preview-convert`, formData, {
        headers: {
          ...getAuthHeader(),
          "Content-Type": "multipart/form-data",
        },
      });
      setPreviewMarkdown(response.data.body_markdown || "");
      if (!uploadMeta.title && response.data.title_hint) {
        setUploadMeta((prev) => ({ ...prev, title: response.data.title_hint }));
      }
      toast.success(`Preview ready (${response.data.char_count || 0} chars)`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Preview conversion failed");
    }
  };

  const submitUpload = async (event) => {
    event.preventDefault();
    if (!uploadFile) {
      toast.error("Choose a document first");
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append("file", uploadFile);
    formData.append("title", uploadMeta.title || "");
    formData.append("summary", uploadMeta.summary || "");
    formData.append("category", uploadMeta.category || "Other");
    formData.append("tags", uploadMeta.tags || "");
    formData.append("sort_order", String(Number(uploadMeta.sort_order) || 0));
    formData.append("is_active", String(Boolean(uploadMeta.is_active)));
    try {
      const response = await axios.post(`${API_URL}/api/knowledge/articles/upload`, formData, {
        headers: {
          ...getAuthHeader(),
          "Content-Type": "multipart/form-data",
        },
      });
      if (response.data.conversion_status === "failed") {
        toast.error(response.data.conversion_error || "Conversion failed");
      } else {
        toast.success("Document converted to markdown and saved");
      }
      setUploadDialogOpen(false);
      setUploadFile(null);
      setPreviewMarkdown("");
      setUploadMeta({
        title: "",
        summary: "",
        category: "Other",
        tags: "",
        sort_order: 0,
        is_active: true,
      });
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const reconvertArticle = async (articleId) => {
    try {
      await axios.post(
        `${API_URL}/api/knowledge/articles/${articleId}/reconvert`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success("Article reconverted from original file");
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Reconvert failed");
    }
  };

  const filteredFaqs = faqs.filter((faq) => {
    const q = faqSearch.toLowerCase();
    return (
      !q ||
      String(faq.question || "").toLowerCase().includes(q) ||
      String(faq.category || "").toLowerCase().includes(q) ||
      formatTags(faq.tags).toLowerCase().includes(q)
    );
  });

  const filteredArticles = articles.filter((article) => {
    const q = articleSearch.toLowerCase();
    return (
      !q ||
      String(article.title || "").toLowerCase().includes(q) ||
      String(article.category || "").toLowerCase().includes(q) ||
      formatTags(article.tags).toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-6 animate-fade-in" data-testid="knowledge-base-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">Knowledge Base</h1>
          <p className="text-muted-foreground mt-1">
            Manage FAQs and documents. Uploads are converted to compact markdown for the chatbot.
          </p>
        </div>
      </div>

      <Tabs defaultValue="faqs" className="space-y-4">
        <TabsList className="rounded-none bg-muted/60">
          <TabsTrigger value="faqs" className="rounded-none" data-testid="tab-faqs">
            FAQs
          </TabsTrigger>
          <TabsTrigger value="articles" className="rounded-none" data-testid="tab-articles">
            Articles & Documents
          </TabsTrigger>
        </TabsList>

        <TabsContent value="faqs" className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            <div className="relative max-w-md w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={faqSearch}
                onChange={(e) => setFaqSearch(e.target.value)}
                placeholder="Search FAQs..."
                className="pl-9 rounded-none"
                data-testid="faq-search"
              />
            </div>
            {canEdit && (
              <Dialog
                open={faqDialogOpen}
                onOpenChange={(open) => {
                  setFaqDialogOpen(open);
                  if (!open) resetFaqForm();
                }}
              >
                <DialogTrigger asChild>
                  <Button className="btn-gradient rounded-none" data-testid="add-faq-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Add FAQ
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl rounded-none">
                  <DialogHeader>
                    <DialogTitle className="font-['Manrope']">
                      {editingFaq ? "Edit FAQ" : "Add FAQ"}
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={submitFaq} className="space-y-4 mt-2">
                    <div className="space-y-2">
                      <Label>Question *</Label>
                      <Input
                        value={faqForm.question}
                        onChange={(e) => setFaqForm({ ...faqForm, question: e.target.value })}
                        className="rounded-none"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Answer *</Label>
                      <Textarea
                        value={faqForm.answer}
                        onChange={(e) => setFaqForm({ ...faqForm, answer: e.target.value })}
                        className="rounded-none min-h-[140px]"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Category</Label>
                        <Select
                          value={faqForm.category}
                          onValueChange={(value) => setFaqForm({ ...faqForm, category: value })}
                        >
                          <SelectTrigger className="rounded-none">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {categories.map((cat) => (
                              <SelectItem key={cat} value={cat}>
                                {cat}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Sort order</Label>
                        <Input
                          type="number"
                          value={faqForm.sort_order}
                          onChange={(e) => setFaqForm({ ...faqForm, sort_order: e.target.value })}
                          className="rounded-none"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Tags (comma separated)</Label>
                      <Input
                        value={faqForm.tags}
                        onChange={(e) => setFaqForm({ ...faqForm, tags: e.target.value })}
                        className="rounded-none"
                        placeholder="parking, wifi"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={faqForm.is_active}
                        onCheckedChange={(checked) => setFaqForm({ ...faqForm, is_active: checked })}
                      />
                      <Label>Active (used by chatbot)</Label>
                    </div>
                    <Button type="submit" className="btn-gradient rounded-none w-full">
                      {editingFaq ? "Save FAQ" : "Create FAQ"}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>

          <Card className="rounded-none">
            <CardHeader>
              <CardTitle className="font-['Manrope'] flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                FAQs ({filteredFaqs.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-muted-foreground">Loading...</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Question</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredFaqs.map((faq) => (
                      <TableRow key={faq.id}>
                        <TableCell className="max-w-md">
                          <div className="font-medium">{faq.question}</div>
                          <div className="text-xs text-muted-foreground line-clamp-2 mt-1">
                            {faq.answer}
                          </div>
                        </TableCell>
                        <TableCell>{faq.category}</TableCell>
                        <TableCell>
                          <Badge variant={faq.is_active ? "default" : "secondary"} className="rounded-none">
                            {faq.is_active ? "Active" : "Inactive"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right space-x-1">
                          {canEdit && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditFaq(faq)}
                              data-testid={`edit-faq-${faq.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => deleteFaq(faq.id)}
                              data-testid={`delete-faq-${faq.id}`}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {!filteredFaqs.length && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-muted-foreground">
                          No FAQs yet
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="articles" className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            <div className="relative max-w-md w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={articleSearch}
                onChange={(e) => setArticleSearch(e.target.value)}
                placeholder="Search articles..."
                className="pl-9 rounded-none"
                data-testid="article-search"
              />
            </div>
            {canEdit && (
              <div className="flex gap-2">
                <Dialog
                  open={uploadDialogOpen}
                  onOpenChange={(open) => {
                    setUploadDialogOpen(open);
                    if (!open) {
                      setUploadFile(null);
                      setPreviewMarkdown("");
                    }
                  }}
                >
                  <DialogTrigger asChild>
                    <Button variant="outline" className="rounded-none" data-testid="upload-doc-btn">
                      <FileUp className="h-4 w-4 mr-2" />
                      Upload document
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-3xl rounded-none max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="font-['Manrope']">
                        Upload PDF / DOCX / TXT / MD / HTML
                      </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={submitUpload} className="space-y-4 mt-2">
                      <div className="space-y-2">
                        <Label>Document *</Label>
                        <Input
                          type="file"
                          accept=".pdf,.docx,.txt,.md,.markdown,.html,.htm"
                          className="rounded-none"
                          onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                          required
                        />
                        <p className="text-xs text-muted-foreground">
                          Converted to compact markdown for lower LLM token use. Max 12MB.
                        </p>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Title</Label>
                          <Input
                            value={uploadMeta.title}
                            onChange={(e) => setUploadMeta({ ...uploadMeta, title: e.target.value })}
                            className="rounded-none"
                            placeholder="Auto from filename if empty"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Category</Label>
                          <Select
                            value={uploadMeta.category}
                            onValueChange={(value) => setUploadMeta({ ...uploadMeta, category: value })}
                          >
                            <SelectTrigger className="rounded-none">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {categories.map((cat) => (
                                <SelectItem key={cat} value={cat}>
                                  {cat}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Summary</Label>
                        <Input
                          value={uploadMeta.summary}
                          onChange={(e) => setUploadMeta({ ...uploadMeta, summary: e.target.value })}
                          className="rounded-none"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Tags (comma separated)</Label>
                        <Input
                          value={uploadMeta.tags}
                          onChange={(e) => setUploadMeta({ ...uploadMeta, tags: e.target.value })}
                          className="rounded-none"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={uploadMeta.is_active}
                          onCheckedChange={(checked) =>
                            setUploadMeta({ ...uploadMeta, is_active: checked })
                          }
                        />
                        <Label>Active after conversion</Label>
                      </div>
                      {previewMarkdown && (
                        <div className="space-y-2">
                          <Label>Markdown preview</Label>
                          <Textarea
                            value={previewMarkdown}
                            readOnly
                            className="rounded-none min-h-[180px] font-mono text-xs"
                          />
                        </div>
                      )}
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          className="rounded-none"
                          onClick={previewConvert}
                        >
                          Preview convert
                        </Button>
                        <Button
                          type="submit"
                          className="btn-gradient rounded-none flex-1"
                          disabled={uploading}
                        >
                          {uploading ? "Uploading..." : "Convert & save"}
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>

                <Dialog
                  open={articleDialogOpen}
                  onOpenChange={(open) => {
                    setArticleDialogOpen(open);
                    if (!open) resetArticleForm();
                  }}
                >
                  <DialogTrigger asChild>
                    <Button className="btn-gradient rounded-none" data-testid="add-article-btn">
                      <Plus className="h-4 w-4 mr-2" />
                      Add article
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-3xl rounded-none max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="font-['Manrope']">
                        {editingArticle ? "Edit article" : "Add article"}
                      </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={submitArticle} className="space-y-4 mt-2">
                      <div className="space-y-2">
                        <Label>Title *</Label>
                        <Input
                          value={articleForm.title}
                          onChange={(e) => setArticleForm({ ...articleForm, title: e.target.value })}
                          className="rounded-none"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Summary</Label>
                        <Input
                          value={articleForm.summary}
                          onChange={(e) => setArticleForm({ ...articleForm, summary: e.target.value })}
                          className="rounded-none"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Body (markdown) *</Label>
                        <Textarea
                          value={articleForm.body_markdown}
                          onChange={(e) =>
                            setArticleForm({ ...articleForm, body_markdown: e.target.value })
                          }
                          className="rounded-none min-h-[220px] font-mono text-sm"
                          required
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Category</Label>
                          <Select
                            value={articleForm.category}
                            onValueChange={(value) =>
                              setArticleForm({ ...articleForm, category: value })
                            }
                          >
                            <SelectTrigger className="rounded-none">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {categories.map((cat) => (
                                <SelectItem key={cat} value={cat}>
                                  {cat}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Sort order</Label>
                          <Input
                            type="number"
                            value={articleForm.sort_order}
                            onChange={(e) =>
                              setArticleForm({ ...articleForm, sort_order: e.target.value })
                            }
                            className="rounded-none"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Tags (comma separated)</Label>
                        <Input
                          value={articleForm.tags}
                          onChange={(e) => setArticleForm({ ...articleForm, tags: e.target.value })}
                          className="rounded-none"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={articleForm.is_active}
                          onCheckedChange={(checked) =>
                            setArticleForm({ ...articleForm, is_active: checked })
                          }
                        />
                        <Label>Active (used by chatbot)</Label>
                      </div>
                      <Button type="submit" className="btn-gradient rounded-none w-full">
                        {editingArticle ? "Save article" : "Create article"}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            )}
          </div>

          <Card className="rounded-none">
            <CardHeader>
              <CardTitle className="font-['Manrope']">Articles ({filteredArticles.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-muted-foreground">Loading...</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Title</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Conversion</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredArticles.map((article) => (
                      <TableRow key={article.id}>
                        <TableCell className="max-w-md">
                          <div className="font-medium">{article.title}</div>
                          <div className="text-xs text-muted-foreground line-clamp-2 mt-1">
                            {article.summary || article.body_markdown}
                          </div>
                          {article.source_filename && (
                            <div className="text-xs text-muted-foreground mt-1">
                              Source: {article.source_filename}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>{article.category}</TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              article.conversion_status === "ready"
                                ? "default"
                                : article.conversion_status === "failed"
                                  ? "destructive"
                                  : "secondary"
                            }
                            className="rounded-none"
                          >
                            {article.conversion_status || "manual"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={article.is_active ? "default" : "secondary"}
                            className="rounded-none"
                          >
                            {article.is_active ? "Active" : "Inactive"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right space-x-1">
                          {canEdit && article.source_storage_path && (
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Reconvert from original"
                              onClick={() => reconvertArticle(article.id)}
                            >
                              <RefreshCw className="h-4 w-4" />
                            </Button>
                          )}
                          {canEdit && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditArticle(article)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => deleteArticle(article.id)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {!filteredArticles.length && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground">
                          No articles yet
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
